import Drawer from '@mui/material/Drawer';
import assert from 'assert';
import type { LegacyRef } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getProjectFromSites } from '../../../store/config';
import { isDataState } from '../../../store/data/state';
import { useAppSelector } from '../../../store/hooks';
import type { NavButtonProps } from '../../utils/nav/NavButtonComponent';
import NavButtonComponent from '../../utils/nav/NavButtonComponent';
import NavSectionComponent from '../../utils/nav/NavSectionComponent';
import VerticalNavComponent from '../../utils/nav/VerticalNavComponent';
import PermissionDecoratorV2 from '../../utils/permission/PermissionDecoratorV2';
import StateDecorator from '../../utils/state/StateDecorator';

export interface LeftNavButtonGroupType {
  icon: string;
  name: string;
  subs: NavButtonProps[];
  open?: boolean;
}

export interface NavBarProps {
  project_id?: number;
  site_id?: number;
  show?: boolean;
  onToggleClick?: () => void;
}

const includesAsURI = (includingPath: string, includedPath: string) =>
  includingPath.includes(encodeURI(includedPath));

export const backofficeNav: NavButtonProps[] = [
  {
    name: 'Projets',
    url: '/management/projects',
    itemName: 'configuration_project_tab',
  },
  {
    name: 'Sites',
    url: '/management/sites',
    itemName: 'configuration_site_tab',
  },
  { name: 'Alertes', url: '/management/alerts' },
  {
    name: 'Inventaire des dispositifs de mesure',
    url: '/management/devices',
    itemName: 'configuration_devices_tab',
  },
  {
    name: 'Capteurs',
    url: '/management/devices',
  },
  {
    name: 'Utilisateurs',
    url: '/management/users',
    itemName: 'configuration_users_tab',
  },
];

function LeftNavComponent(
  { project_id, site_id, show = true, onToggleClick }: NavBarProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const { t } = useTranslation('menu');
  assert(!site_id || !isNaN(site_id));

  const { pathname } = useLocation();

  const projects = useAppSelector(
    (state) => state.data_slice.externals.projects
  );
  const sites = useAppSelector((state) => state.data_slice.externals.sites);
  const project_sites = useAppSelector(
    (state) => state.data_slice.externals.project_sites
  );

  const siteIdsToDashboardIds = useAppSelector(
    (state) => state.data_slice.externals.siteIdsToDashboardIds
  );

  const actions = useAppSelector((state) => state.data_slice.externals.actions);

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={show}
      onClose={() => onToggleClick?.()}
      sx={{ width: 240, flexShrink: 0 }}
      ModalProps={{ keepMounted: true }}
    >
      <VerticalNavComponent ref={ref} id="left-nav" side="left" open={true}>
        <NavSectionComponent>
          {StateDecorator(
            [sites, projects, project_sites, siteIdsToDashboardIds, actions],
            ref,
            () => {
              assert(isDataState(sites));
              assert(isDataState(projects));
              assert(isDataState(project_sites));
              assert(isDataState(siteIdsToDashboardIds));
              assert(isDataState(actions));

              const site = sites.data[site_id!];
              assert(site);

              const project = getProjectFromSites(
                site,
                project_sites,
                projects
              )[0];
              assert(project);

              const siteIdToGridId = Object.entries(
                siteIdsToDashboardIds.data.values
              ).reduce((acc, [siteId, gridIds]) => {
                return gridIds[0]
                  ? {
                      ...acc,
                      [siteId]:
                        gridIds.find(
                          (dashboard) => dashboard.type === 'user_dashboard'
                        )?.id || 0,
                    }
                  : acc;
              }, {} as Record<number, number>);

              const siteIdToReportId = Object.entries(
                siteIdsToDashboardIds.data.values
              ).reduce((acc, [siteId, gridIds]) => {
                return gridIds[0]
                  ? {
                      ...acc,
                      [siteId]:
                        gridIds.find(
                          (dashboard) => dashboard.type === 'report_dashboard'
                        )?.id || 0,
                    }
                  : acc;
              }, {} as Record<number, number>);

              return (
                <ul
                  className="uk-list uk-list-collapse"
                  style={{ marginTop: 0 }}
                >
                  <PermissionDecoratorV2
                    item={'dashboard_tab'}
                    project_id={project.id}
                    site_id={site.id}
                  >
                    <NavButtonComponent
                      name={t('Tableaux de bord')}
                      url={`/projects/${
                        project.id
                      }/sites/${site_id}/tableau-de-bord/${
                        site_id ? siteIdToGridId[site_id] ?? 0 : 0
                      }`}
                      active={includesAsURI(
                        pathname,
                        `/projects/${project.id}/sites/${site_id}/tableau-de-bord`
                      )}
                    />
                  </PermissionDecoratorV2>
                  <PermissionDecoratorV2
                    item={'device_sites_tab'}
                    project_id={project.id}
                    site_id={site.id}
                  >
                    <NavButtonComponent
                      name={t('Stations de mesure')}
                      url={`/projects/${project.id}/sites/${site_id}/stations-de-mesures`}
                      active={includesAsURI(
                        pathname,
                        `/projects/${project.id}/sites/${site_id}/stations-de-mesures`
                      )}
                    />
                  </PermissionDecoratorV2>
                  <PermissionDecoratorV2
                    item={'alerts_tab'}
                    project_id={project.id}
                    site_id={site.id}
                  >
                    <NavButtonComponent
                      name={t('Alertes')}
                      url={`/projects/${project.id}/sites/${site_id}/alertes`}
                      active={includesAsURI(
                        pathname,
                        `/projects/${project.id}/sites/${site_id}/alertes`
                      )}
                    />
                  </PermissionDecoratorV2>
                  <PermissionDecoratorV2
                    item={'campaigns_tab'}
                    project_id={project.id}
                    site_id={site.id}
                  >
                    <NavButtonComponent
                      name={t('Campagnes')}
                      url={`/projects/${project.id}/sites/${site_id}/campagnes`}
                      active={includesAsURI(
                        pathname,
                        `/projects/${project.id}/sites/${site_id}/campagnes`
                      )}
                    />
                  </PermissionDecoratorV2>
                  <PermissionDecoratorV2 project_id={project.id} site_id={site_id} item={'measure_data_tab'}>
                    <NavButtonComponent
                      name={t('Données de mesures')}
                      url={`/projects/${project.id}/sites/${site_id}/données-de-mesures`}
                      active={includesAsURI(
                        pathname,
                        `/projects/${project.id}/sites/${site_id}/données-de-mesures`
                      )}
                    />
                  </PermissionDecoratorV2>
                  <PermissionDecoratorV2 project_id={project.id} site_id={site_id} item={'reports_tab'}>
                    <NavButtonComponent
                      name={t('Rapports')}
                      url={`/projects/${project.id}/sites/${site_id}/rapports/${
                        site_id ? siteIdToReportId[site_id] ?? 0 : 0
                      }`}
                      active={includesAsURI(
                        pathname,
                        `/projects/${project.id}/sites/${site_id}/rapports`
                      )}
                    />
                  </PermissionDecoratorV2>
                </ul>
              );
            }
          )}
        </NavSectionComponent>
      </VerticalNavComponent>
    </Drawer>
  );
}

export default forwardRef(LeftNavComponent);
